/**
 * @file LinkSettingsPreview.tsx
 * @brief DEV ONLY: Super Admin → Links & company info with a stubbed backend
 *        (`/__preview/link-settings`, W19 24-10-2026)
 *
 * @details
 * Seeded from BACKEND linkSettings.mjs as prod is today (store links and
 * the registered address empty). GET and PUT are answered inside the page,
 * so Save is safe here. `?reject=1` = the server refuses the Save
 * (INVALID_LINK_VALUE on the first changed key); `?missing=1` = a backend
 * before B23 (404).
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiClientError } from "@/shared/api/client";
import { __setLinkSettingsStub, type LinkSettingsBody } from "../api";
import { LinkSettingsPage } from "../LinkSettingsPage";
import type { LinkSection, LinkSetting } from "../linkSettings";

const params = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
const REJECT = params.get("reject") === "1";
/** ?oldsocial=1 = a backend before B24 (four networks only). */
const OLD_SOCIAL = params.get("oldsocial") === "1";
const MISSING = params.get("missing") === "1";

const row = (key: string, label: string, kind: string, def: string, extra: Partial<LinkSetting> = {}): LinkSetting => ({
  key,
  label,
  kind,
  value: def,
  default: def,
  saved: false,
  ...extra,
});

const SEED: LinkSection[] = [
  {
    id: "downloads",
    label: "Downloads",
    settings: [
      row("APP_STORE_URL", "App Store (iPhone) link", "url", ""),
      row("GOOGLE_PLAY_URL", "Google Play (Android) link", "url", ""),
      row("MAC_DOWNLOAD_URL", "Mac download page", "url", "https://verblizr.com/download/mac/"),
      row("MAC_ARM64_DMG_URL", "Mac installer, Apple silicon (.dmg)", "url", "https://downloads.verblizr.com/desktop/VERBLIZR-arm64.dmg"),
      row("MAC_X64_DMG_URL", "Mac installer, Intel (.dmg)", "url", "https://downloads.verblizr.com/desktop/VERBLIZR-x64.dmg"),
      row("WINDOWS_DOWNLOAD_URL", "Windows download", "url", ""),
    ],
  },
  {
    id: "sites",
    label: "Sites",
    settings: [
      row("WEB_PANEL_URL", "Web app", "url", "https://admin.verblizr.com"),
      row("WEBSITE_URL", "Website", "url", "https://verblizr.com"),
      row("HELP_URL", "Help centre", "url", "https://verblizr.com/support/"),
      row("PRIVACY_URL", "Privacy policy", "url", "https://verblizr.com/privacy/"),
      row("TERMS_URL", "Terms", "url", "https://verblizr.com/terms/"),
      row("DELETE_ACCOUNT_URL", "Delete my account page", "url", "https://verblizr.com/delete-account/"),
      row("DPA_URL", "Data processing agreement", "url", ""),
    ],
  },
  {
    id: "contact",
    label: "Contact",
    settings: [
      row("SUPPORT_EMAIL", "Support email", "email", "support@verblizr.com"),
      row("SALES_EMAIL", "Sales email", "email", "sales@verblizr.co.uk"),
      row("CONTACT_PHONE", "Contact phone", "phone", ""),
      row("CONTACT_PAGE_URL", "Contact page", "url", "https://verblizr.com/contact/"),
    ],
  },
  {
    id: "company",
    label: "Company",
    settings: [
      row("COMPANY_LEGAL_NAME", "Company legal name", "text", "VERBLIZR Ltd"),
      row("COMPANY_NUMBER", "Company number", "text", "17067773"),
      row("COMPANY_ADDRESS", "Registered address (shows the legal line in emails and invoices)", "text", ""),
    ],
  },
  {
    id: "social",
    label: "Social",
    settings: [
      row("LINKEDIN_URL", "LinkedIn", "url", ""),
      row("X_URL", "X", "url", ""),
      row("YOUTUBE_URL", "YouTube", "url", ""),
      row("INSTAGRAM_URL", "Instagram", "url", ""),
      ...(OLD_SOCIAL
        ? []
        : [
            row("FACEBOOK_URL", "Facebook", "url", ""),
            row("TIKTOK_URL", "TikTok", "url", ""),
            row("THREADS_URL", "Threads", "url", ""),
            row("WHATSAPP_URL", "WhatsApp", "url", ""),
          ]),
    ],
  },
  {
    id: "app",
    label: "App versions",
    settings: [
      row("MIN_APP_VERSION_IOS", "Oldest iPhone app version allowed", "version", ""),
      row("MIN_APP_VERSION_ANDROID", "Oldest Android app version allowed", "version", ""),
      row("MIN_APP_VERSION_MAC", "Oldest Mac app version allowed", "version", ""),
    ],
  },
  {
    id: "features",
    label: "Features shown to users",
    settings: [
      row("INTERPRETATION_ON_PHONE", "Interpretation on the phone", "choice", "live", { options: ["live", "coming_soon", "hidden"] }),
      row("REALTIME_VISIBLE", "Realtime interpretation visible", "bool", "false"),
    ],
  },
];

function makeStub() {
  let sections = SEED.map((s) => ({ ...s, settings: s.settings.map((x) => ({ ...x })) }));
  const body = (changed: LinkSettingsBody["changed"] = []): LinkSettingsBody => ({ sections, changed });
  return {
    get: async () => {
      if (MISSING) throw new ApiClientError("Not found", 404);
      return body();
    },
    put: async (patch: Record<string, string | null>) => {
      await new Promise((r) => setTimeout(r, 400));
      const keys = Object.keys(patch);
      if (REJECT && keys.length) {
        throw new ApiClientError("Some values were not accepted.", 400, "INVALID_LINK_VALUE", {
          code: "INVALID_LINK_VALUE",
          invalid: [{ key: keys[0], reason: "must start with https://" }],
        });
      }
      const changed: NonNullable<LinkSettingsBody["changed"]> = [];
      sections = sections.map((sec) => ({
        ...sec,
        settings: sec.settings.map((x) => {
          if (!(x.key in patch)) return x;
          const v = patch[x.key];
          changed.push({ key: x.key, from: x.value, to: v });
          return v === null ? { ...x, value: x.default, saved: false } : { ...x, value: v, saved: true };
        }),
      }));
      return body(changed);
    },
  };
}

export function LinkSettingsPreviewPage() {
  const [client] = useState(() => new QueryClient({ defaultOptions: { queries: { retry: false } } }));
  useState(() => __setLinkSettingsStub(makeStub()));
  return (
    <QueryClientProvider client={client}>
      <p className="px-4 pt-3 text-xs text-muted-foreground">
        Preview: GET and PUT are answered inside this page, so Save sends nothing. ?reject=1 · ?missing=1
      </p>
      <LinkSettingsPage />
    </QueryClientProvider>
  );
}
